/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import DateRangePicker from "components/DateRangePicker";
import Button from "components/Button";
import { ExportXLSX, rearrangeCSVColumns } from "utils/table-utils";
import moment from "moment";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";
import get from "lodash/get";
import mapValues from "lodash/mapValues";
import MuiSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "components/Autocomplete";
import { ALL_DATE_RANGE, utcToLocal } from "utils/dates";
import Modal from "components/CustomModal";
import { setuxvalue, getuxvalue } from "handlers/ux";
import {
  convertAmountWithDecimal,
  replaceQuotes,
  removeEmptyValueFromObject,
} from "utils";
import TokenManager from "utils/TokenManager";
import {
  enableWPSPG,
  enableFortisPG,
  enableStripePG,
  enableEasyPayPG,
  enableForVoltyx,
  enableWorldPayPG,
  enableCustomerInvoiceRestrictedCreation,
  isWileyClient,
  enableSAP_DPA,
  enableDEVandDemoOnly,
  enableUseCreditMemoPayment,
} from "handlers/features";
import Dropdown from "components/Button/Dropdown";
import { getLocaleString } from "utils/localization/locale";
import SaveSearchModal from "components/SaveSearchModal";
import { saveSearch } from "containers/saved_search/actions";
import omit from "lodash/omit";
import { getCustomerList } from "./actions";
import { DEFAULT_PAYMENT_TYPE, getPaymentActionColumnLabel } from "./constants";

export const initialPaymentListFilters = {
  status: "",
  page: 1,
  from_date: moment().subtract(30, "days").format("MM/DD/YYYY"),
  to_date: moment().format("MM/DD/YYYY"),
  sort_by: "latest_status_date",
  sort_order: "desc",
  customerSelect: null,
  interval: "",
  payment_type: enableEasyPayPG() ? DEFAULT_PAYMENT_TYPE : "",
  last_updated_from: ALL_DATE_RANGE[0],
  last_updated_to: ALL_DATE_RANGE[1],
  lastUpdatedInterval: "All",
  date_type: "",
};

const getParams = (filters) => {
  const { ignoreURIencode, ...restFilters } = filters;
  return removeEmptyValueFromObject(restFilters);
};

function PaymentFilterSelect({
  children,
  className = "t-14",
  MenuProps = {},
  ...props
}) {
  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <MuiSelect
        className={className}
        displayEmpty
        fullWidth
        size="small"
        MenuProps={{ disablePortal: true, ...MenuProps }}
        {...props}
      >
        {children}
      </MuiSelect>
    </div>
  );
}

function FilterAndExport(props) {
  const {
    onChange,
    columnOrder,
    visibleColumns,
    isErpPushEnabled,
    hideCustomerFilter,
    isInCustomerPortal,
    erpType,
    paymentAnalytics: { listData, filters },
    setPaymentsListFilter = () => {},
  } = props;

  const isOpenFilterOnRedirection =
    localStorage.getItem("open_filter") === "Payments";
  const [showFilter, setShowFilter] = useState(isOpenFilterOnRedirection);
  if (isOpenFilterOnRedirection) {
    localStorage.setItem("open_filter", "");
  }
  const [showExport, setShowExport] = useState(false);
  const [interval, setInterval] = useState(get(filters, "interval", ""));
  const [lastUpdatedInterval, setlastUpdatedInterval] = useState(
    get(filters, "lastUpdatedInterval", "All")
  );
  const [from_date, setFromDate] = useState(get(filters, "from_date", ""));
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [saveSearchReqObj, setSaveSearchReqObj] = useState({
    id: get(filters, "saved_search_details.search_id", ""),
    name: "",
    filters: {
      ...getParams({
        ...omit(filters, [
          "saved_search_details",
          "page",
          "limit",
          "sort_by",
          "sort_order",
          "is_export",
        ]),
        ignoreURIencode: true,
      }),
      interval: filters.interval || "",
    },
    user_id: TokenManager.getUserId(),
    client_id: TokenManager.getClientId(),
    search_type: "Payments",
    access_type: "Public",
    ...get(filters, "saved_search_details", {}),
  });

  const [to_date, setToDate] = useState(get(filters, "to_date", ""));
  const [date_type, setDateType] = useState(get(filters, "date_type", ""));
  const [status, setStatus] = useState(
    get(props, "paymentAnalytics.filters.status", "")
  );
  const [payment_type, setPaymentType] = useState(
    enableEasyPayPG() ||
      enableWorldPayPG() ||
      enableWPSPG() ||
      enableStripePG() ||
      enableFortisPG()
      ? get(filters, "payment_type", DEFAULT_PAYMENT_TYPE)
      : ""
  );
  const [last_updated_from, setLastUpdatedFrom] = useState(
    get(
      filters,
      "last_updated_from",
      initialPaymentListFilters.last_updated_from
    )
  );
  const [last_updated_to, setLastUpdatedTo] = useState(
    get(filters, "last_updated_to", initialPaymentListFilters.last_updated_to)
  );
  const [erp_sync_status, setErpSyncStatus] = useState(
    get(filters, "erp_sync_status", "")
  );
  const [isCustomerFetching, setIsCustomerFetching] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [customerSelect, setCustomerSelect] = useState(
    get(filters, "customerSelect", null)
  );
  const [exporting, setExporting] = useState(false);
  const [columnsCount, setColumnsCount] = useState(0);
  const [visibleColumnsShow, setVisibleColumnsShow] = useState(false);
  const [exportAllColumns, setExportAllColumns] = useState(
    get(getuxvalue("EXPORT_ALL_COLUMNS"), "reportsPaymentsList", false)
  );
  const [showFilterModal, setShowFilterModal] = useState(false);
  const debounceMethod = useRef(
    debounce((text) => searchCustomer(text), 500)
  ).current;

  const showTrasactionTypeFilter =
    enableEasyPayPG() ||
    enableWorldPayPG() ||
    enableStripePG() ||
    enableWPSPG() ||
    enableFortisPG();

  const csvRef = useRef(null);

  const isMobile = window.innerWidth <= 767;

  const showRefundButton =
    !isWileyClient() &&
    listData.some((data) =>
      enableEasyPayPG()
        ? get(data, "latest_status", "") === "SETTLED"
        : enableWorldPayPG() || enableWPSPG()
          ? get(data, "latest_status", "") === "PAID"
          : enableStripePG()
            ? ["PAID", "SETTLED"].includes(get(data, "latest_status", ""))
            : enableFortisPG()
              ? ["PAID", "SETTLED"].includes(get(data, "latest_status", ""))
              : false
    );

  const showVoidButton =
    enableFortisPG() &&
    listData.some(
      (data) =>
        get(data, "payment_method", "") !== "CREDIT_MEMO" &&
        get(data, "payment_type", "") !== "REFUND" &&
        get(data, "latest_status", "") === "PROCESSING" &&
        get(data, "external_payment_id", "")
    );
  const paymentActionColumnLabel = getPaymentActionColumnLabel();
  const showPaymentActionButton = showRefundButton || showVoidButton;

  const isRefundTransactions = payment_type === "REFUND";
  const isAutoPayTransactions = payment_type === "AUTO_PAY";
  const isCreditMemoTransactions = payment_type === "CREDIT_MEMO";

  useEffect(() => {
    const newCol = Object.keys(visibleColumns).filter(
      (key) => visibleColumns[key]
    );
    const columnsCount = Object.keys(newCol).length;
    setColumnsCount(columnsCount);
  }, []);

  // Sync local state when filters prop changes (e.g., from saved search)
  // Only sync when filters actually change, not when reopening dropdown
  useEffect(() => {
    // Don't reset local state if the filter dropdown is currently open
    if (showFilter) return;

    setFromDate(get(filters, "from_date", ""));
    setToDate(get(filters, "to_date", ""));
    setDateType(get(filters, "date_type", ""));
    setStatus(get(filters, "status", ""));
    setLastUpdatedFrom(
      get(
        filters,
        "last_updated_from",
        initialPaymentListFilters.last_updated_from
      )
    );
    setLastUpdatedTo(
      get(filters, "last_updated_to", initialPaymentListFilters.last_updated_to)
    );
    setErpSyncStatus(get(filters, "erp_sync_status", ""));
    setCustomerSelect(get(filters, "customerSelect", null));
    setInterval(get(filters, "interval", ""));
    setlastUpdatedInterval(get(filters, "lastUpdatedInterval", "All"));
    if (showTrasactionTypeFilter) {
      setPaymentType(get(filters, "payment_type", DEFAULT_PAYMENT_TYPE));
    }
  }, [filters, showFilter]);

  function searchCustomer(text) {
    setIsCustomerFetching(true);
    if (text) {
      getCustomerList(text, (customerList) => {
        setCustomerList(customerList);
        setIsCustomerFetching(false);
      });
    } else {
      setCustomerList([]);
      setIsCustomerFetching(false);
    }
  }

  const setDates = (from_date, to_date, interval = "") => {
    setFromDate(from_date.format("MM/DD/YYYY"));
    setToDate(to_date.format("MM/DD/YYYY"));
    setInterval(interval);
  };

  const setLastUpdatedDates = (
    last_updated_from,
    last_updated_to,
    interval = ""
  ) => {
    setLastUpdatedFrom(last_updated_from.format("MM/DD/YYYY"));
    setLastUpdatedTo(last_updated_to.format("MM/DD/YYYY"));
    setlastUpdatedInterval(interval);
  };

  const setTransactionType = (payment_type) => {
    setPaymentType(payment_type);
  };

  const onReset = () => {
    const { client_customer_rel_id } = filters;
    const initialPaymentListFiltersCopy = { ...initialPaymentListFilters };
    if (isInCustomerPortal || client_customer_rel_id) {
      initialPaymentListFiltersCopy.from_date = ALL_DATE_RANGE[0];
      initialPaymentListFiltersCopy.to_date = ALL_DATE_RANGE[1];
    }
    setShowFilter(false);
    setShowExport(false);
    setErpSyncStatus("");
    setIsCustomerFetching(false);
    setExporting(false);
    setShowFilterModal(false);
    setStatus(initialPaymentListFiltersCopy.status);
    setFromDate(initialPaymentListFiltersCopy.from_date);
    setToDate(initialPaymentListFiltersCopy.to_date);
    setLastUpdatedFrom(initialPaymentListFiltersCopy.last_updated_from);
    setLastUpdatedTo(initialPaymentListFiltersCopy.last_updated_to);
    setCustomerSelect(initialPaymentListFiltersCopy.customerSelect);
    setInterval(initialPaymentListFiltersCopy.interval);
    setPaymentType(initialPaymentListFiltersCopy.payment_type);
    setlastUpdatedInterval(initialPaymentListFiltersCopy.lastUpdatedInterval);
    setDateType(initialPaymentListFiltersCopy.date_type);
    props.getPaymentsData({
      ...initialPaymentListFiltersCopy,
      client_customer_rel_id: hideCustomerFilter ? client_customer_rel_id : "",
      is_customer_invoice_restricted_role:
        enableCustomerInvoiceRestrictedCreation() && TokenManager.isCustomer()
          ? TokenManager.isCustomerInvoiceRestricted()
            ? "1"
            : "0"
          : "",
    });
    props.resetPaymentsListReducer({ filter: initialPaymentListFiltersCopy });
  };

  const getData = (is_export = false, cb) => {
    const { client_customer_rel_id } = filters;
    const params = {
      from_date,
      to_date,
      date_type,
      erp_sync_status,
      last_updated_from,
      last_updated_to,
    };

    if (status || filters.status) {
      // Credit memos are always PAID — don't send status filter for them
      if (payment_type !== "CREDIT_MEMO") {
        params.status = status;
      }
    }
    if (customerSelect || client_customer_rel_id) {
      params.client_customer_rel_id = client_customer_rel_id
        ? client_customer_rel_id
        : customerSelect.id;
      params.customerSelect = customerSelect;
    }
    if (is_export || filters.is_export) {
      params.is_export = is_export;
    }
    if (interval || filters.interval) {
      params.interval = interval;
    }
    if (lastUpdatedInterval && lastUpdatedInterval !== "All") {
      params.lastUpdatedInterval = lastUpdatedInterval;
    }
    if (showTrasactionTypeFilter) {
      // Always send payment_type so switching back to "All" (empty string) clears the previous filter
      params.payment_type = payment_type;
    }

    // Update save search object with current filters
    setSaveSearchReqObj((prev) => ({
      ...prev,
      filters: {
        ...getParams({ ...params, ignoreURIencode: true }),
        interval,
        lastUpdatedInterval,
      },
    }));

    const finalPayload = {
      ...filters,
      ...params,
      is_customer_invoice_restricted_role:
        enableCustomerInvoiceRestrictedCreation() && TokenManager.isCustomer()
          ? TokenManager.isCustomerInvoiceRestricted()
            ? "1"
            : "0"
          : "",
      page: 1,
    };

    props.getPaymentsData(finalPayload).then((finalData) => {
      setShowFilter(false);
      setShowExport(false);
      setExporting(false);
      setShowFilterModal(false);
      setPaymentsListFilter(finalPayload);

      cb && cb(finalData);
    });
  };

  const setExportAllOption = (value) => {
    setExportAllColumns(value);
    setuxvalue("EXPORT_ALL_COLUMNS", {
      ...getuxvalue("EXPORT_ALL_COLUMNS"),
      reportsPaymentsList: value,
    });
  };

  const exportAllData = (flag) => {
    setExporting(true);
    getData(true, (finalData) => {
      if (window.Cypress) {
        return;
      }
      const exportData = isAutoPayTransactions
        ? finalData.filter((d) => get(d, "is_auto_paid", false))
        : finalData;
      if (flag === true && csvRef && csvRef.current) {
        csvRef.current.link.click();
      } else {
        ExportXLSX(
          ExportCSV(
            exportData,
            visibleColumns,
            isInCustomerPortal,
            exportAllColumns,
            columnOrder,
            isRefundTransactions
          ),
          `Payments_${moment().format("MMMM Do YYYY-h:mm:ss-a")}.xlsx`
        );
      }
    });
  };

  return (
    <div className="row mb-4 mt-4">
      <div className="col-md-6 d-flex col">
        <Dropdown
          showDropdown={showFilter}
          buttonProps={{
            as: "span",
            role: "button",
            "data-toggle": "dropdown",
            "aria-haspopup": "true",
            "aria-expanded": "false",
          }}
          btnClassName="dropdown-toggle cursor-pointer"
          setShowDropdown={(value) => {
            isMobile ? setShowFilterModal(value) : setShowFilter(value);
          }}
          buttonText={getLocaleString("common.filter", "Filter")}
          dropdownWrapperClassName="filterdroplist p-2 width-500px"
          dropdownWrapperProps={{
            id: "outerside",
          }}
          filterButton
        >
          <div className="row" id="filter-form">
            <div className="col-md-6">
              <div className="form-group mb-2">
                <label>{getLocaleString("common.dateType", "Date Type")}</label>
                <PaymentFilterSelect
                  data-cy="date_type"
                  value={date_type}
                  onChange={(e) => setDateType(e.target.value)}
                >
                  <MenuItem value="">
                    {getLocaleString(
                      "payments.initiated_date",
                      "Initiated Date"
                    )}
                  </MenuItem>
                  <MenuItem value="payout_date">
                    {getLocaleString("payments.payout_date", "Payout Date")}
                  </MenuItem>
                </PaymentFilterSelect>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-2">
                <label>{getLocaleString("common.date", "Date")}</label>
                <div
                  data-cy="date_picker"
                  className="single-date-picker t-12 overflow-hidden"
                >
                  <DateRangePicker
                    allToDate={true}
                    label={interval}
                    startDate={moment(from_date)}
                    endDate={moment(to_date)}
                    getSelectedDates={setDates}
                    parentEl="#outerside"
                    className="single-date-picker"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-0">
                <label>
                  {getLocaleString(
                    "payments.last_updated_date",
                    "Last Updated Date"
                  )}
                </label>
                <div className="single-date-picker t-12 overflow-hidden">
                  <DateRangePicker
                    allToDate={true}
                    label={lastUpdatedInterval}
                    startDate={moment(last_updated_from)}
                    endDate={moment(last_updated_to)}
                    getSelectedDates={setLastUpdatedDates}
                    parentEl="#outerside"
                    className="single-date-picker"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>{getLocaleString("common.status", "Status")}</label>
                <PaymentFilterSelect
                  data-cy="payment_status"
                  value={isCreditMemoTransactions ? "PAID" : status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={isCreditMemoTransactions}
                  title={
                    isCreditMemoTransactions
                      ? getLocaleString(
                          "payments.credit_memo_always_paid",
                          "Credit memo applications are always Paid"
                        )
                      : ""
                  }
                >
                  <MenuItem value="">
                    {getLocaleString("common.all", "All")}
                  </MenuItem>
                  <MenuItem value="INITIATED">
                    {getLocaleString("common.initiated", "Initiated")}
                  </MenuItem>
                  <MenuItem value="PROCESSING">
                    {getLocaleString("common.processing", "Processing")}
                  </MenuItem>
                  <MenuItem value="PAID">
                    {getLocaleString("common.paid", "Paid")}
                  </MenuItem>
                  <MenuItem value="REQUIRES_ACTION">
                    {getLocaleString(
                      "payments.requires_action",
                      "Requires Action"
                    )}
                  </MenuItem>
                  <MenuItem value="FAILED">
                    {getLocaleString("common.failed", "Failed")}
                  </MenuItem>
                  <MenuItem value="SETTLED">
                    {getLocaleString("common.settled", "Settled")}
                  </MenuItem>
                </PaymentFilterSelect>
              </div>
            </div>
            {!hideCustomerFilter ? (
              <div className="col-md-6 mt-2">
                <div className="form-group ">
                  <div className="pagepayable" data-cy="customer">
                    <label className="">
                      {getLocaleString("common.customer", "Customer")}
                    </label>
                    <Autocomplete
                      suggestions={uniqBy(
                        customerList.map((s) => ({
                          id: s.client_customer_rel.id,
                          name: s.customer.name,
                        })),
                        "id"
                      )}
                      getSelected={(item) => setCustomerSelect(item)}
                      search={(text) => debounceMethod(text)}
                      isLoading={isCustomerFetching}
                      defaultValue={customerSelect && customerSelect.name}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="" />
            )}
            {showTrasactionTypeFilter ? (
              <div className="col-md-6 mt-2">
                <div className="form-group ">
                  <label>
                    {getLocaleString(
                      "payments.transaction_type",
                      "Transaction Type"
                    )}
                  </label>
                  <PaymentFilterSelect
                    data-cy="transactionType"
                    value={payment_type}
                    onChange={(e) => setTransactionType(e.target.value)}
                  >
                    <MenuItem value="">
                      {getLocaleString("common.all", "All")}
                    </MenuItem>
                    <MenuItem value="AUTO_PAY">
                      {getLocaleString("payments.is_auto_paid", "Auto Pay")}
                    </MenuItem>
                    {!enableWPSPG() ? (
                      <MenuItem value="REFUND">
                        {getLocaleString("payments.refund", "Refund")}
                      </MenuItem>
                    ) : (
                      <></>
                    )}
                    <MenuItem value={DEFAULT_PAYMENT_TYPE}>
                      {getLocaleString("payments.payment", "Payment")}
                    </MenuItem>
                    {enableUseCreditMemoPayment() && (
                      <MenuItem value="CREDIT_MEMO">
                        {getLocaleString("payments.credit_memo", "Credit Memo")}
                      </MenuItem>
                    )}
                  </PaymentFilterSelect>
                </div>
              </div>
            ) : isErpPushEnabled ? null : (
              <div className="col-md-6" />
            )}

            {isErpPushEnabled ? (
              <div className="col-md-6 mt-2">
                <div className="form-group">
                  <label>
                    {getLocaleString(
                      "payments.posting_status",
                      "Posting Status"
                    )}
                  </label>
                  <PaymentFilterSelect
                    data-testid="transactionType"
                    value={erp_sync_status}
                    onChange={(e) => setErpSyncStatus(e.target.value)}
                  >
                    <MenuItem value="">
                      {getLocaleString("common.all", "All")}
                    </MenuItem>
                    <MenuItem value="1">
                      {getLocaleString(
                        "payments.pushed_to_erp",
                        "Pushed to ERP"
                      )}
                    </MenuItem>
                    <MenuItem value="0">
                      {getLocaleString("Not pushed to ERP")}
                    </MenuItem>
                  </PaymentFilterSelect>
                </div>
              </div>
            ) : (
              <div className="col-md-6" />
            )}

            <div className="" />

            <div className="col-md-6">
              {enableDEVandDemoOnly() && TokenManager.getAllAdmins() ? (
                <div className=" setbtn mt-3">
                  <Button
                    type="button"
                    variant="textual-secondary"
                    className="t-14"
                    data-cy="save_search"
                    onClick={() => setShowSearchModal(!showSearchModal)}
                  >
                    {getLocaleString(
                      "saved_search.save_this_search",
                      "Save This Search"
                    )}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="col-md-6">
              <div className="setbtn mt-3 d-flex gap-2 align-items-center">
                <Button
                  type="button"
                  variant="outline-primary"
                  data-testid="resetbtn"
                  className="resetbtn"
                  onClick={onReset}
                >
                  {getLocaleString("common.reset", "Reset")}
                </Button>
                <Button
                  type="button"
                  data-testid="applybtn"
                  className="applybtn"
                  onClick={() => getData()}
                >
                  {getLocaleString("common.submit", "Submit")}
                </Button>
              </div>
            </div>
          </div>
        </Dropdown>
        <Dropdown
          showDropdown={visibleColumnsShow}
          buttonProps={{
            as: "span",
            role: "button",
            "data-toggle": "dropdown",
            "aria-haspopup": "true",
            "aria-expanded": "false",
            "data-cy": "columns-dropdown",
          }}
          btnClassName="ms-2"
          setShowDropdown={(show) => setVisibleColumnsShow(show)}
          buttonText={getLocaleString("common.columns", "Columns")}
          dropdownWrapperClassName="p-0"
          dropdownWrapperProps={{
            id: "companylist",
            "data-cy": "payment_columns",
            style: {
              maxHeight: "50vh",
              overflowY: "auto",
              minWidth: "13rem",
            },
          }}
          columnsButton
        >
          {Object.keys(visibleColumns)
            .filter((a) => {
              if (
                (a === paymentActionColumnLabel && !showPaymentActionButton) ||
                (a === "Transaction ID" && !enableWorldPayPG()) ||
                (a === "Receipt" && (isWileyClient() || enableSAP_DPA()))
              ) {
                return false;
              }
              if (
                isRefundTransactions &&
                ["Net Amount", paymentActionColumnLabel].includes(a)
              ) {
                return false;
              }
              if (
                a === "Posting Status" &&
                !["NETSUITE", "SAP_BTP"].includes(erpType)
              ) {
                return false;
              }
              return true;
            })
            .map((acc, index) => (
              <span
                key={index}
                className="bg-option-selected-grey"
                onClick={() => {
                  if (visibleColumns[acc] && columnsCount === 1) return;
                  const colCount = visibleColumns[acc]
                    ? columnsCount - 1
                    : columnsCount + 1;
                  setColumnsCount(colCount);
                  const _columns = {
                    ...visibleColumns,
                    [acc]: !visibleColumns[acc],
                  };
                  onChange(_columns);
                  setuxvalue("REPORTS_PAYMENT_COLUMNS", _columns);
                }}
              >
                <input
                  type="checkbox"
                  className="me-2 d-inline-block align-middle"
                  name={acc}
                  disabled={columnsCount === 1}
                  checked={visibleColumns[acc]}
                  data-tlabel={`checkbox ${acc}`}
                  data-cy="column-check-payables"
                />
                <span className={"d-inline-block align-middle p-0 w-80"}>
                  {acc}
                </span>
              </span>
            ))}
        </Dropdown>
      </div>
      <div className="col-md-6 col d-flex justify-content-end">
        <div className="m-2">
          {props.paymentAnalytics.total ? (
            <span className="">
              {getLocaleString("common.total", "Total")}:{" "}
              {props.paymentAnalytics.total}
            </span>
          ) : null}
        </div>
        <CSVLink
          data={ExportCSV(
            isAutoPayTransactions
              ? listData.filter((d) => get(d, "is_auto_paid", false))
              : listData,
            visibleColumns,
            isInCustomerPortal,
            exportAllColumns,
            columnOrder,
            isRefundTransactions
          )}
          ref={csvRef}
          filename={`Payments_${moment().format("MMMM Do YYYY-h:mm:ss-a")}.csv`}
          style={{ display: "none" }}
        />
        <Dropdown
          showDropdown={showExport}
          buttonProps={{
            as: "span",
            role: "button",
            "data-toggle": "dropdown",
            "aria-haspopup": "true",
            "aria-expanded": "false",
            "data-cy": "export",
            endIcon: exporting && <i className="fa fa-spinner fa-spin" />,
          }}
          setShowDropdown={() => setShowExport(!showExport)}
          buttonText={getLocaleString("common.exportTitle", "Export")}
          dropdownWrapperClassName="dropdown-menu-end right-0 payexpdroplist"
          dropdownWrapperProps={{
            id: "payment-export",
          }}
        >
          {enableForVoltyx() && (
            <span className="t-12 d-block text-end cursor-pointer mb-1 me-2">
              <label className="m-0">
                <input
                  checked={exportAllColumns}
                  onChange={(e) => setExportAllOption(e.target.checked)}
                  type="checkbox"
                  className="align-middle me-1"
                />{" "}
                <span className="align-middle">
                  {getLocaleString("common.all_data", "All Data")}
                </span>
              </label>
            </span>
          )}
          <span
            data-cy="exportCSV"
            className="t-12 d-block text-end pe-2 mb-1 cursor-pointer"
            onClick={() => exportAllData(true)}
          >
            {getLocaleString("common.exportCSV", "Export CSV")}
          </span>
          <span
            data-cy="exportXls"
            className="t-12 d-block text-end pe-2 cursor-pointer"
            onClick={() => exportAllData(false)}
          >
            {getLocaleString("common.exportExcel", "Export Excel")}
          </span>
        </Dropdown>
      </div>
      <Modal
        footer={() => (
          <>
            <Button
              type="button"
              variant="outline-primary"
              data-testid="resetbtn"
              className="resetbtn"
              onClick={onReset}
            >
              {getLocaleString("common.reset", "Reset")}
            </Button>
            <Button
              type="button"
              data-testid="applybtn"
              className="applybtn"
              onClick={() => getData()}
            >
              {getLocaleString("common.submit", "Submit")}
            </Button>
          </>
        )}
        title="Filter"
        size="md"
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
      >
        <div
          className="row"
          style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
        >
          <div className="col-12">
            <div className="form-group">
              <label>{getLocaleString("common.dateType", "Date Type")}</label>
              <PaymentFilterSelect
                data-cy="date_type_mobile"
                value={date_type}
                onChange={(e) => setDateType(e.target.value)}
              >
                <MenuItem value="">
                  {getLocaleString("payments.initiated_date", "Initiated Date")}
                </MenuItem>
                <MenuItem value="payout_date">
                  {getLocaleString("payments.payout_date", "Payout Date")}
                </MenuItem>
              </PaymentFilterSelect>
            </div>
          </div>
          <div className="col-12" id="mobile-picker">
            <div className="form-group">
              <label>{getLocaleString("common.date", "Date")}</label>
              <div className="single-date-picker t-12 overflow-hidden">
                <DateRangePicker
                  all={true}
                  label={interval}
                  startDate={moment(from_date)}
                  endDate={moment(to_date)}
                  getSelectedDates={setDates}
                  className="single-date-picker"
                  parentEl="#mobile-picker"
                />
              </div>
            </div>
          </div>
          <div className="col-12" id="mobile-picker">
            <div className="form-group">
              <label>
                {getLocaleString(
                  "payments.last_updated_date",
                  "Last Updated Date"
                )}
              </label>
              <div className="single-date-picker t-12 overflow-hidden">
                <DateRangePicker
                  all={true}
                  label={lastUpdatedInterval}
                  startDate={moment(last_updated_from)}
                  endDate={moment(last_updated_to)}
                  getSelectedDates={setLastUpdatedDates}
                  className="single-date-picker"
                  parentEl="#mobile-picker"
                />
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>{getLocaleString("common.status", "Status")}</label>
              <PaymentFilterSelect
                data-testid="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">
                  {getLocaleString("common.all", "All")}
                </MenuItem>
                <MenuItem value="INITIATED">
                  {getLocaleString("common.Initiated", "Initiated")}
                </MenuItem>
                <MenuItem value="PROCESSING">
                  {getLocaleString("common.processing", "Processing")}
                </MenuItem>
                <MenuItem value="PAID">
                  {getLocaleString("common.paid", "Paid")}
                </MenuItem>
                <MenuItem value="REQUIRES_ACTION">
                  {getLocaleString(
                    "payments.requires_action",
                    "Requires Action"
                  )}
                </MenuItem>
                <MenuItem value="FAILED">
                  {getLocaleString("common.failed", "Failed")}
                </MenuItem>
                {enableEasyPayPG() && (
                  <MenuItem value="SETTLED">
                    {getLocaleString("common.settled", "Settled")}
                  </MenuItem>
                )}
              </PaymentFilterSelect>
            </div>
          </div>
          {showTrasactionTypeFilter && (
            <div className="col-12">
              <div className="form-group">
                <label>
                  {getLocaleString(
                    "payments.transaction_type",
                    "Transaction Type"
                  )}
                </label>
                <PaymentFilterSelect
                  data-testid="transactionType"
                  value={payment_type}
                  onChange={(e) => setTransactionType(e.target.value)}
                >
                  <MenuItem value="">
                    {getLocaleString("common.all", "All")}
                  </MenuItem>
                  <MenuItem value="AUTO_PAY">
                    {getLocaleString("payments.is_auto_paid", "Auto Pay")}
                  </MenuItem>
                  {!enableWPSPG() ? (
                    <MenuItem value="REFUND">
                      {getLocaleString("payments.refund", "Refund")}
                    </MenuItem>
                  ) : (
                    <></>
                  )}
                  <MenuItem value={DEFAULT_PAYMENT_TYPE}>
                    {getLocaleString("payments.payment", "Payment")}
                  </MenuItem>
                </PaymentFilterSelect>
              </div>
            </div>
          )}
        </div>
      </Modal>
      <SaveSearchModal
        saveSearch={saveSearch}
        showSearchModal={showSearchModal}
        saveSearchReqObj={saveSearchReqObj}
        setShowSearchModal={setShowSearchModal}
        setSaveSearchReqObj={setSaveSearchReqObj}
      />
    </div>
  );
}

export default FilterAndExport;

function ExportCSV(
  data,
  visibleColumns,
  isInCustomerPortal = false,
  exportAllColumns,
  columnOrder,
  isRefundTransaction = false
) {
  const currencyCode = TokenManager.getCurrencyCode();
  const isWPSPG = enableWPSPG();

  const showFailureColumn =
    data.length &&
    data.some((d) =>
      ["FAILED", "REQUIRES_ACTION"].includes(get(d, "latest_status", ""))
    );

  let visibleHeaders = {
    [getLocaleString("common.customer", "Customer")]:
      visibleColumns["Customer"],
    [getLocaleString("common.invoices", "Invoices")]:
      visibleColumns["Invoices"],
    [getLocaleString("payments.autopaid_invoice", "Autopaid Invoice")]:
      isInCustomerPortal && !isRefundTransaction,
    [getLocaleString("payments.transaction_id", "Transaction ID")]:
      enableWorldPayPG() && visibleColumns["Transaction ID"],
    [getLocaleString("payments.payout_id", "Payout ID")]:
      visibleColumns["Payout ID"],
    [getLocaleString("payments.payout_id", "Payout Date")]:
      visibleColumns["Payout Date"],
    [getLocaleString("payments.payout_amount", "Payout Amount")]:
      visibleColumns["Payout Amount"],
    [getLocaleString("common.currency", "Currency")]:
      visibleColumns["Currency"],
    [isRefundTransaction ? "Refund Amount" : "Amount Paid"]:
      (!isInCustomerPortal ? true : isRefundTransaction) &&
      visibleColumns["Show Payment Details"],
    [getLocaleString("payments.fees_paid", "Fees Paid")]:
      !isInCustomerPortal &&
      !isRefundTransaction &&
      visibleColumns["Show Payment Details"],
    [isInCustomerPortal ? "Amount Paid" : "Total Paid"]:
      visibleColumns["Show Payment Details"] && !isRefundTransaction,
    [getLocaleString("payments.fees_deducted", "Fees Deducted")]:
      !isInCustomerPortal &&
      visibleColumns["Show Payment Details"] &&
      !isRefundTransaction,
    [getLocaleString("payments.net_amount", "Net Amount")]:
      !isInCustomerPortal &&
      visibleColumns["Net Amount"] &&
      !isRefundTransaction,
    [getLocaleString("common.paymentMethod", "Payment Method")]:
      visibleColumns["Payment Method"],
    [getLocaleString("payments.card_last_4", "Card Last 4")]:
      visibleColumns[getLocaleString("payments.card_last_4", "Card Last 4")],
    [getLocaleString("payments.initiated_date", "Initiated Date")]:
      visibleColumns["Initiated Date"],
    [getLocaleString("payments.last_updated_at", "Last Updated At")]:
      visibleColumns["Last Updated At"],
    [getLocaleString("payments.posting_status", "Posting Status")]:
      visibleColumns["Posting Status"],
    [getLocaleString(
      "payments.posting_status_reason",
      "Posting Status Reason"
    )]: visibleColumns["Posting Status"],
    [getLocaleString("common.status", "Status")]: visibleColumns["Status"],
    [getLocaleString("common.message", "Message")]:
      !isInCustomerPortal &&
      data.length &&
      visibleColumns["Message"] &&
      showFailureColumn,
    [getLocaleString("payments.deduction_code", "Deduction Code")]:
      visibleColumns[
        getLocaleString("payments.deduction_code", "Deduction Code")
      ],
    [getLocaleString("payments.payment_note", "Payment Note")]: true,
    [getLocaleString("payments.transaction_type", "Transaction Type")]:
      visibleColumns["Transaction Type"],
    [getLocaleString("payments.payer_name", "Payer Name")]:
      visibleColumns["Payer Name"],
    [getLocaleString("payments.auth_number", "Auth Number")]:
      enableSAP_DPA() &&
      visibleColumns[getLocaleString("payments.auth_number", "Auth Number")],
    [getLocaleString("payments.auth_date", "Auth Date")]:
      enableSAP_DPA() &&
      visibleColumns[getLocaleString("payments.auth_date", "Auth Date")],
  };

  if (isWPSPG) {
    // update visible header for wps to get column invoice/document instead of invoice and remove invoices
    visibleHeaders = {
      [getLocaleString("common.invoice_or_document", "Invoice/Document")]:
        visibleColumns["Invoice/Document"],
      ...visibleHeaders,
    };
    delete visibleHeaders[getLocaleString("common.invoices", "Invoices")];
  }

  if (exportAllColumns) {
    visibleHeaders = mapValues(visibleHeaders, () => true);
  }
  if (!data.length) {
    let emptyEle = {
      Customer: "",
      Invoices: "",
      "Autopaid Invoice": "",
      "Transaction ID": "",
      "Payout ID": "",
      "Payout Date": "",
      "Payout Amount": "",
      [isRefundTransaction ? "Refund Amount" : "Amount Paid"]: "",
      "Fees Paid": "",
      "Total Paid": "",
      "Fees Deducted": "",
      "Net Amount": "",
      "Payment Method": "",
      [getLocaleString("payments.card_last_4", "Card Last 4")]: "",
      "Initiated Date": "",
      "Last Updated At": "",
      "Posting Status": "",
      "Posting Status Reason": "",
      Status: "",
      Message: "",
      Currency: "",
      "Deduction Code": "",
      "Payment Note": "",
      "Transaction Type": "",
      "Payer Name": "",
      ...(enableSAP_DPA() && {
        [getLocaleString("payments.auth_number", "Auth Number")]: "",
        [getLocaleString("payments.auth_date", "Auth Date")]: "",
      }),
    };

    if (isWPSPG) {
      // delete invoices and replace with invoice/document for wps
      delete emptyEle["Invoices"];
      emptyEle = {
        ...emptyEle,
        [getLocaleString("common.invoice_or_document", "Invoice/Document")]: "",
      };
    }
    return [filterProperties(emptyEle, visibleHeaders)];
  }

  const csvData = data.map((element) => {
    const len = get(element, "session_history", []).length - 1;
    const sessionHistory = get(element, "session_history", []);
    const paymentStatus = get(element, "latest_status", "");
    const last4 = sessionHistory.find((s) => get(s, "event_metadata.last4"))
      ?.event_metadata.last4;
    const auth_date = sessionHistory.find((s) =>
      get(s, "event_metadata.auth_date")
    )?.event_metadata.auth_date;
    const auth_number = sessionHistory.find((s) =>
      get(s, "event_metadata.dpa_auth_number")
    )?.event_metadata.dpa_auth_number;
    const isCreditMemo = get(element, "payment_method", "") === "CREDIT_MEMO";

    const ele = {
      Customer: replaceQuotes(
        get(element, "client_customer_rel.associated_customer.name", "-")
      ),
      [getLocaleString("common.invoices", "Invoices")]: get(
        element,
        "payment_session_ar_invoice",
        []
      )
        .map((d) => get(d, "invoice.invoice_number"))
        .join(", "),
      [getLocaleString("payments.autopaid_invoice", "Autopaid Invoice")]: get(
        element,
        "is_auto_paid",
        false
      )
        ? "Yes"
        : "No",
      [getLocaleString("payments.transaction_id", "Transaction ID")]: get(
        element,
        "external_payment_id",
        "-"
      ),
      [getLocaleString("payments.payout_id", "Payout ID")]: get(
        element,
        "payout.external_payout_id"
      ),
      [getLocaleString("payments.payout_date", "Payout Date")]: isCreditMemo
        ? "-"
        : utcToLocal(get(element, "payout.payout_date", "")),
      [getLocaleString("payments.payout_amount", "Payout Amount")]: isCreditMemo
        ? "-"
        : convertAmountWithDecimal(
            get(element, "payout.payout_amount"),
            get(element, "currency", currencyCode) || currencyCode
          ),
      [getLocaleString("common.currency", "Currency")]: get(
        element,
        "currency"
      ),
      [isRefundTransaction ? "Refund Amount" : "Amount Paid"]:
        convertAmountWithDecimal(
          get(element, "total_transaction_amount", 0) || 0,
          get(element, "currency", currencyCode) || currencyCode
        ),
      [getLocaleString("payments.fees_paid", "Fees Paid")]: isCreditMemo
        ? "-"
        : convertAmountWithDecimal(
            get(element, "customer_total_transaction_fees_paid", 0) || 0,
            get(element, "currency", currencyCode) || currencyCode
          ),
      [isInCustomerPortal ? "Amount Paid" : "Total Paid"]: isCreditMemo
        ? "-"
        : convertAmountWithDecimal(
            get(element, "total_amount_paid", 0) || 0,
            get(element, "currency", currencyCode) || currencyCode
          ),
      [getLocaleString("payments.fees_deducted", "Fees Deducted")]: isCreditMemo
        ? "-"
        : convertAmountWithDecimal(
            get(element, "total_transaction_fees", 0),
            get(element, "currency", currencyCode) || currencyCode
          ),
      [getLocaleString("payments.net_amount", "Net Amount")]: isCreditMemo
        ? "-"
        : convertAmountWithDecimal(
            get(element, "amount_received", 0),
            get(element, "currency", currencyCode) || currencyCode
          ),
      [getLocaleString("common.paymentMethod", "Payment Method")]: get(
        element,
        "payment_method",
        "-"
      ),
      [getLocaleString("payments.card_last_4", "Card Last 4")]:
        ["PAID", "SETTLED"].includes(paymentStatus) && last4
          ? `**** ${last4}`
          : "-",
      [getLocaleString("payments.initiated_date", "Initiated Date")]:
        utcToLocal(get(element, "initiated_date", "")),
      [getLocaleString("payments.last_updated_at", "Last Updated At")]:
        utcToLocal(get(element, "latest_status_date", "")),
      [getLocaleString("payments.posting_status", "Posting Status")]:
        isCreditMemo
          ? "-"
          : ["PAID", "SETTLED"].includes(get(element, "latest_status", ""))
            ? get(element, "erp_sync_status", "")
              ? "Pushed to ERP"
              : "Not pushed to ERP"
            : "-",
      [getLocaleString(
        "payments.posting_status_reason",
        "Posting Status Reason"
      )]: isCreditMemo ? "-" : sessionHistory?.[0]?.erp_sync_reason || "-",
      [getLocaleString("common.status", "Status")]: get(
        element,
        "latest_status",
        "-"
      ),
      [getLocaleString("common.message", "Message")]: [
        "FAILED",
        "REQUIRES_ACTION",
      ].includes(get(element, "latest_status", ""))
        ? get(element, `session_history[${len}].message`, "-")
        : "-",
      [getLocaleString("payments.deduction_code", "Deduction Code")]:
        get(element, "payment_session_ar_invoice", [])?.length &&
        get(element, "payment_session_ar_invoice", [])
          .map((d) =>
            get(d, "dispute_reason_code.name", "")
              ? `${get(d, "invoice.invoice_number", "")}: ${get(d, "dispute_reason_code.name", "")}`
              : ""
          )
          .filter((d) => d)
          .join(),
      [getLocaleString("payments.payment_note", "Payment Note")]: get(
        element,
        "payment_note",
        "-"
      ),
      [getLocaleString("payments.transaction_type", "Transaction Type")]: get(
        element,
        "is_auto_paid",
        false
      )
        ? "Auto Pay"
        : get(element, "payment_type") || DEFAULT_PAYMENT_TYPE,
      [getLocaleString("payments.payer_name", "Payer Name")]: get(
        element,
        "user_info.name",
        "-"
      ),
      ...(enableSAP_DPA() && {
        [getLocaleString("payments.auth_number", "Auth Number")]:
          auth_number || "-",
        [getLocaleString("payments.auth_date", "Auth Date")]: auth_date || "-",
      }),
    };

    if (isWPSPG) {
      ele[getLocaleString("common.invoice_or_document", "Invoice/Document")] =
        ele[getLocaleString("common.invoices", "Invoices")];
      delete ele[getLocaleString("common.invoices", "Invoices")];
    }
    const obj = filterProperties(ele, visibleHeaders);
    return rearrangeCSVColumns(columnOrder, obj);
  });

  return csvData;
}

function filterProperties(originalObj, visibleHeaders) {
  Object.keys(visibleHeaders).forEach((key) => {
    if (!visibleHeaders[key]) delete originalObj[key];
  });
  return originalObj;
}
